import React, { useState, useContext } from "react";
import { ethers } from "ethers";
import { WalletContext } from "../context/WalletContext";
import Header from "../components/Header";
import CardSuccessModal from "../components/CardSuccessModal";

const API_URL =
  process.env.REACT_APP_API_URL || "https://bitspend-api.onrender.com";

export default function RegisterCard() {
  const {
    selectedWallet,
    provider,
    signer,
    // isCorrectNetwork,  // REMOVED
    // switchToMezoTestnet  // REMOVED
  } = useContext(WalletContext);

  // Form state
  const [dailyLimit, setDailyLimit] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [statusMessage, setStatusMessage] = useState("");

  // Card data
  const [cardData, setCardData] = useState(null);

  /**
   * Ensure correct network before transaction
   */
  const ensureCorrectNetwork = async () => {
    try {
      if (!provider) {
        throw new Error("No provider available");
      }

      // Check current network
      const network = await provider.getNetwork();
      const currentChainId = network.chainId.toString();

      console.log("Current network:", currentChainId);

      if (currentChainId !== "31611") {
        console.log("Wrong network, switching to Mezo...");

        // Request switch
        try {
          await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: "0x7B7B" }],
          });

          // Wait for switch
          await new Promise((resolve) => setTimeout(resolve, 2000));

          // Verify switch
          const newNetwork = await provider.getNetwork();
          if (newNetwork.chainId.toString() !== "31611") {
            throw new Error("Failed to switch to Mezo testnet");
          }

          console.log("✅ Switched to Mezo testnet");
        } catch (switchError) {
          if (switchError.code === 4902) {
            // Network not added, add it
            await window.ethereum.request({
              method: "wallet_addEthereumChain",
              params: [
                {
                  chainId: "0x7B7B",
                  chainName: "Mezo Testnet",
                  nativeCurrency: {
                    name: "MEZO",
                    symbol: "MEZO",
                    decimals: 18,
                  },
                  rpcUrls: ["https://rpc.test.mezo.org"],
                  blockExplorerUrls: ["https://explorer.test.mezo.org"],
                },
              ],
            });

            await new Promise((resolve) => setTimeout(resolve, 2000));
          } else {
            throw switchError;
          }
        }
      }

      return true;
    } catch (err) {
      console.error("Network switch failed:", err);
      throw new Error("Please switch to Mezo Testnet in your wallet");
    }
  };

  const prepareRegistration = async () => {
    try {
      const cardId = `${Date.now()}`;

      setStatusMessage("Preparing card registration...");

      const response = await fetch(
        `${API_URL}/api/cards/prepare-registration`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            cardId,
            pin,
            dailyLimit,
            ownerAddress: selectedWallet,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to prepare registration");
      }

      const data = await response.json();
      return data.data;
    } catch (err) {
      throw new Error(`Preparation failed: ${err.message}`);
    }
  };

  const signTransactions = async (preparedData) => {
    const sendWithRetry = async (txParams, description) => {
      setStatusMessage(`Please approve ${description} in your wallet...`);

      let attempts = 0;
      const maxAttempts = 2;

      while (attempts < maxAttempts) {
        try {
          const currentSigner =
            attempts === 0
              ? signer
              : await (async () => {
                await new Promise((resolve) => setTimeout(resolve, 1000));
                const freshProvider = new ethers.BrowserProvider(provider);
                return await freshProvider.getSigner();
              })();

          const tx = await currentSigner.sendTransaction(txParams);
          setStatusMessage(
            `${description} sent: ${tx.hash.substring(0, 10)}...`,
          );
          await tx.wait();
          setStatusMessage(`${description} confirmed! ✓`);
          return tx.hash;
        } catch (error) {
          attempts++;

          if (error.code === "NETWORK_ERROR" && attempts < maxAttempts) {
            console.log(
              `Network error on ${description}, retrying (attempt ${attempts + 1})...`,
            );
            continue;
          }

          if (error.code === 4001 || error.code === "ACTION_REJECTED") {
            throw new Error("Transaction rejected by user");
          }

          throw error;
        }
      }
    };

    try {
      const registerTxHash = await sendWithRetry(
        {
          to: preparedData.registerTransaction.to,
          data: preparedData.registerTransaction.data,
          gasLimit: preparedData.registerTransaction.gasLimit,
        },
        "registration transaction",
      );

      await new Promise((resolve) => setTimeout(resolve, 500));

      const gasTxHash = await sendWithRetry(
        {
          to: preparedData.gasFundingTransaction.to,
          value: preparedData.gasFundingTransaction.value,
          gasLimit: preparedData.gasFundingTransaction.gasLimit,
        },
        "gas funding transaction",
      );

      return { registerTxHash, gasTxHash };
    } catch (err) {
      throw new Error(`Transaction failed: ${err.message}`);
    }
  };
  const confirmRegistration = async (preparedData, txHashes) => {
    try {
      setStatusMessage("Confirming registration...");

      const response = await fetch(
        `${API_URL}/api/cards/confirm-registration`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            cardId: preparedData.cardId,
            registerTxHash: txHashes.registerTxHash,
            gasTxHash: txHashes.gasTxHash,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to confirm registration");
      }

      const data = await response.json();
      return data.data;
    } catch (err) {
      throw new Error(`Confirmation failed: ${err.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setStep(2);

    try {
      // Validation
      if (pin !== confirmPin) {
        throw new Error("PIN and Confirm PIN do not match!");
      }

      if (pin.length !== 4) {
        throw new Error("PIN must be exactly 4 digits");
      }

      if (!selectedWallet) {
        throw new Error("No wallet connected");
      }

      if (!dailyLimit || parseFloat(dailyLimit) <= 0) {
        throw new Error("Daily limit must be greater than 0");
      }

      // CRITICAL: Ensure correct network before starting
      setStatusMessage("Checking network...");
      await ensureCorrectNetwork();

      // Step 1: Prepare registration
      console.log("📝 Step 1: Preparing registration...");
      const preparedData = await prepareRegistration();
      console.log("✅ Card prepared:", preparedData.cardId);

      // Step 2: Sign transactions
      console.log("✍️ Step 2: Signing transactions...");
      const txHashes = await signTransactions(preparedData);
      console.log("✅ Transactions signed");

      // Step 3: Confirm registration
      console.log("📋 Step 3: Confirming registration...");
      const confirmedData = await confirmRegistration(preparedData, txHashes);
      console.log("✅ Registration confirmed");

      // Success!
      setCardData({
        cardId: confirmedData.cardId,
        cardAddress: confirmedData.cardAddress,
        share2: preparedData.nfcData.share2,
        owner: confirmedData.owner,
        dailyLimit: confirmedData.dailyLimit,
        explorerUrl: confirmedData.blockchain.explorerUrl,
      });

      setStep(3);
      setStatusMessage("Card registered successfully! 🎉");
    } catch (err) {
      console.error("Registration error:", err);
      setError(err.message);
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (setter) => (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 4) value = value.slice(0, 4);
    setter(value);
  };

  const truncateAddress = (addr) =>
    addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : "";

  return (
    <div className="min-h-screen flex flex-col items-center bg-gradient-to-b from-gray-900 to-gray-800 text-white p-6 pb-12">
      <Header />

      <h1 className="text-2xl font-semibold my-4 sm:my-6">Register Card</h1>

      {/* REMOVED: Network Warning */}

      {/* Error Message */}
      {error && (
        <div className="w-full max-w-sm mb-4 bg-red-500/20 border border-red-500 rounded-xl p-4">
          <p className="text-red-200 text-sm">⚠️ {error}</p>
        </div>
      )}

      {/* Loading/Status Message */}
      {loading && (
        <div className="w-full max-w-sm mb-4 bg-blue-500/20 border border-blue-500 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
            <p className="text-blue-200 text-sm">{statusMessage}</p>
          </div>
        </div>
      )}

      {/* Step Indicator */}
      {step > 1 && (
        <div className="w-full max-w-sm mb-4 flex justify-between">
          <div
            className={`flex-1 h-1 ${step >= 2 ? "bg-blue-500" : "bg-gray-700"}`}
          ></div>
          <div
            className={`flex-1 h-1 mx-1 ${step >= 3 ? "bg-blue-500" : "bg-gray-700"}`}
          ></div>
        </div>
      )}

      {/* Registration Form */}
      {step === 1 && (
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-sm flex flex-col gap-4"
        >
          <div>
            <label className="text-gray-400 text-sm">
              Owner Wallet Address
            </label>
            <input
              type="text"
              value={truncateAddress(selectedWallet)}
              readOnly
              className="w-full bg-gray-800 py-3 px-4 rounded-xl text-white cursor-not-allowed"
            />
          </div>

          <div>
            <label className="text-gray-400 text-sm">Daily Limit (MUSD)</label>
            <input
              type="number"
              step="0.01"
              min="1"
              value={dailyLimit}
              onChange={(e) => setDailyLimit(e.target.value)}
              placeholder="Enter daily limit"
              className="w-full bg-gray-800 py-3 px-4 rounded-xl text-white"
              required
              disabled={loading}
            />
            <p className="text-gray-500 text-xs mt-1">
              Maximum amount you can spend per day
            </p>
          </div>

          <div>
            <label className="text-gray-400 text-sm">PIN (4 digits)</label>
            <input
              type="password"
              value={pin}
              onChange={handlePinChange(setPin)}
              placeholder="••••"
              inputMode="numeric"
              maxLength={4}
              className="w-full bg-gray-800 py-3 px-4 rounded-xl text-white"
              required
              disabled={loading}
            />
            <p className="text-gray-500 text-xs mt-1">
              You'll need this PIN to authorize payments
            </p>
          </div>

          <div>
            <label className="text-gray-400 text-sm">Confirm PIN</label>
            <input
              type="password"
              value={confirmPin}
              onChange={handlePinChange(setConfirmPin)}
              placeholder="••••"
              inputMode="numeric"
              maxLength={4}
              className="w-full bg-gray-800 py-3 px-4 rounded-xl text-white"
              required
              disabled={loading}
            />
          </div>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mt-2">
            <p className="text-blue-200 text-sm">
              🔐 <strong>Secure Process:</strong>
            </p>
            <ul className="text-blue-200 text-xs mt-2 space-y-1 ml-4 list-disc">
              <li>Your PIN never leaves your device</li>
              <li>You'll sign 2 transactions with your wallet</li>
              <li>Card will be funded with 0.01 MEZO for gas</li>
            </ul>
          </div>

          <button
            type="submit"
            disabled={loading || !selectedWallet}
            className={`mt-4 w-full py-3 rounded-xl text-lg font-bold shadow-lg transition ${loading || !selectedWallet
                ? "bg-gray-600 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
              }`}
          >
            {loading ? "Processing..." : "Register Card"}
          </button>
        </form>
      )}

      {/* Signing Progress */}
      {step === 2 && (
        <div className="w-full max-w-sm">
          <div className="bg-gray-800 rounded-xl p-6 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold mb-2">Signing Transactions</h2>
            <p className="text-gray-400 text-sm mb-4">{statusMessage}</p>
            <div className="space-y-2 text-left text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                <span className="text-gray-300">Step 1: Register card</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gray-600"></div>
                <span className="text-gray-400">Step 2: Fund with gas</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Card Success Popup */}
      {step === 3 && cardData && (
        <CardSuccessModal
          cardId={cardData.cardId}
          cardAddress={cardData.cardAddress}
          share2={cardData.share2}
          owner={cardData.owner}
          dailyLimit={cardData.dailyLimit}
          explorerUrl={cardData.explorerUrl}
          onClose={() => {
            setCardData(null);
            setStep(1);
            setDailyLimit("");
            setPin("");
            setConfirmPin("");
          }}
        />
      )}
    </div>
  );
}
